import { KeyboardEvent, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import AuthContext from '../../../contexts/AuthContext';
import NavegacionContext from '../../../contexts/NavegacionContext';
import getMenuBarItems from '../../../utils/menubar';
import { MenuPantalla, esFavoritable, getGroupInfo, getPantallas } from '../../../utils/menuGroups';
import { normalizarTexto } from '../../../utils/rutaNavegacion';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { ConceptoBusqueda, buscarDondeAparece } from '../../../api/BusquedaApi';

const MAX_PANTALLAS = 8;
const MAX_RECIENTES = 6;
const MIN_LARGO_BUSQUEDA = 3;

interface Opcion {
    id: string;
    titulo: string;
    detalle?: string;
    icono: string;
    href: string;
}

interface Grupo {
    id: string;
    titulo: string;
    subtitulo?: string;
    opciones: Opcion[];
}

const textoPantalla = (p: MenuPantalla): string =>
    [p.group ? getGroupInfo(p.group).title : '', ...p.ruta].filter(Boolean).join(' › ');

/** Pantallas cuyo nombre o sección contienen todas las palabras escritas; primero las que empiezan igual. */
const filtrarPantallas = (pantallas: MenuPantalla[], q: string): MenuPantalla[] => {
    const consulta = normalizarTexto(q);
    const palabras = consulta.split(/\s+/).filter(Boolean);
    const puntaje = (p: MenuPantalla): number => {
        const nombre = normalizarTexto(p.name);
        if (nombre.startsWith(consulta)) return 0;
        return nombre.includes(consulta) ? 1 : 2;
    };
    return pantallas
        .filter((p) => {
            const texto = normalizarTexto(`${p.name} ${textoPantalla(p)}`);
            return palabras.every((palabra) => texto.includes(palabra));
        })
        .sort((a, b) => puntaje(a) - puntaje(b))
        .slice(0, MAX_PANTALLAS);
};

/**
 * Buscador rápido (Ctrl + K): pantallas del menú por nombre y, desde tres
 * caracteres, «dónde aparece» un código —una OP, una guía, un código ISO o
 * hostname de un equipo, un cliente— con un link a cada pantalla donde está.
 */
const BuscadorRapido = (): ReactElement => {
    const { userRoles } = useContext(AuthContext);
    const { recientes } = useContext(NavegacionContext);
    const history = useHistory();
    const inputRef = useRef<HTMLInputElement>(null);
    const listaRef = useRef<HTMLDivElement>(null);
    const [abierto, setAbierto] = useState(false);
    const [q, setQ] = useState('');
    const [activo, setActivo] = useState(0);
    const [remoto, setRemoto] = useState<{ q: string; conceptos: ConceptoBusqueda[]; error?: boolean }>({ q: '', conceptos: [] });
    const ultimaConsulta = useRef(0);

    const pantallas = useMemo(
        () => getPantallas(getMenuBarItems(userRoles)).filter((p) => esFavoritable(p.href)),
        [userRoles]
    );
    const pantallaPorHref = useMemo(() => new Map(pantallas.map((p) => [p.href, p])), [pantallas]);

    useEffect(() => {
        const atajo = (e: globalThis.KeyboardEvent): void => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setAbierto(true);
            }
        };
        document.addEventListener('keydown', atajo);
        return () => document.removeEventListener('keydown', atajo);
    }, []);

    const consulta = q.trim();
    const consultaRetrasada = useDebouncedValue(consulta, 300);
    const buscaRemoto = consultaRetrasada.length >= MIN_LARGO_BUSQUEDA;

    useEffect(() => {
        if (!abierto || !buscaRemoto) return;
        const id = ++ultimaConsulta.current;
        buscarDondeAparece(consultaRetrasada)
            .then(({ data }) => {
                if (id === ultimaConsulta.current) setRemoto({ q: consultaRetrasada, conceptos: Array.isArray(data) ? data : [] });
            })
            .catch(() => {
                if (id === ultimaConsulta.current) setRemoto({ q: consultaRetrasada, conceptos: [], error: true });
            });
    }, [abierto, buscaRemoto, consultaRetrasada]);

    const grupos = useMemo((): Grupo[] => {
        if (!consulta) {
            const opciones = recientes.slice(0, MAX_RECIENTES).map((r) => ({
                id: `reciente-${r.href}`,
                titulo: r.titulo,
                detalle: r.detalle,
                icono: 'fa-history',
                href: r.href,
            }));
            return opciones.length ? [{ id: 'recientes', titulo: 'Recientes', opciones }] : [];
        }

        const resultado: Grupo[] = [];
        // La respuesta solo vale para lo que está escrito: si el usuario siguió tecleando, se espera la nueva.
        if (remoto.q === consulta) {
            remoto.conceptos.forEach((c, i) => {
                // Solo las pantallas que el usuario tiene en su menú.
                const opciones = c.ubicaciones
                    .filter((u) => pantallaPorHref.has(u.pantalla))
                    .map((u, j) => {
                        const pantalla = pantallaPorHref.get(u.pantalla) as MenuPantalla;
                        return {
                            id: `concepto-${i}-${j}`,
                            titulo: u.label,
                            detalle: [pantalla.name, u.detalle].filter(Boolean).join(' · '),
                            icono: pantalla.icon || 'fa-file-o',
                            href: u.href,
                        };
                    });
                if (opciones.length) {
                    resultado.push({ id: `concepto-${i}`, titulo: `${c.tipo} ${c.codigo}`, subtitulo: c.detalle, opciones });
                }
            });
        }

        const opcionesPantallas = filtrarPantallas(pantallas, consulta).map((p) => ({
            id: `pantalla-${p.href}`,
            titulo: p.name,
            detalle: textoPantalla(p),
            icono: p.icon || 'fa-file-o',
            href: p.href,
        }));
        if (opcionesPantallas.length) resultado.push({ id: 'pantallas', titulo: 'Pantallas', opciones: opcionesPantallas });
        return resultado;
    }, [consulta, recientes, remoto, pantallaPorHref, pantallas]);

    const opciones = useMemo(() => grupos.flatMap((g) => g.opciones), [grupos]);
    const esperandoRemoto = consulta.length >= MIN_LARGO_BUSQUEDA && remoto.q !== consulta;

    useEffect(() => setActivo(0), [consulta, remoto]);

    useEffect(() => {
        listaRef.current?.querySelector('.buscador-opcion.activa')?.scrollIntoView({ block: 'nearest' });
    }, [activo]);

    const cerrar = (): void => {
        setAbierto(false);
        setQ('');
    };

    const ir = (opcion?: Opcion): void => {
        if (!opcion) return;
        cerrar();
        history.push(opcion.href);
    };

    const alTeclear = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActivo((a) => Math.min(a + 1, opciones.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActivo((a) => Math.max(a - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            ir(opciones[activo]);
        }
    };

    let indice = -1;

    return (
        <>
            <button type="button" className="btn-buscador" onClick={() => setAbierto(true)} title="Buscar pantallas, OP, guías o equipos (Ctrl + K)">
                <i className="fa fa-search" aria-hidden="true" />
                <span className="btn-buscador-texto">Buscar</span>
                <kbd className="btn-buscador-atajo">Ctrl K</kbd>
            </button>

            <Modal
                show={abierto}
                onHide={cerrar}
                onEntered={() => inputRef.current?.focus()}
                dialogClassName="buscador-dialog"
                contentClassName="buscador-content"
                aria-label="Buscador rápido"
            >
                <div className="buscador-input">
                    <i className="fa fa-search" aria-hidden="true" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={alTeclear}
                        placeholder="Pantalla, OP, guía de despacho, código ISO, cliente…"
                        aria-label="Buscar"
                        aria-controls="buscador-resultados"
                        autoComplete="off"
                        spellCheck={false}
                    />
                    {esperandoRemoto && <span className="spinner-border spinner-border-sm" role="status" aria-label="Buscando" />}
                    <kbd>Esc</kbd>
                </div>

                <div className="buscador-resultados" id="buscador-resultados" ref={listaRef} role="listbox">
                    {grupos.map((g) => (
                        <div key={g.id} className="buscador-grupo">
                            <div className="buscador-grupo-titulo">
                                <span>{g.titulo}</span>
                                {g.subtitulo && <small>{g.subtitulo}</small>}
                            </div>
                            {g.opciones.map((o) => {
                                indice += 1;
                                const i = indice;
                                return (
                                    <button
                                        key={o.id}
                                        type="button"
                                        role="option"
                                        aria-selected={i === activo}
                                        className={classNames('buscador-opcion', { activa: i === activo })}
                                        onMouseMove={() => setActivo(i)}
                                        onClick={() => ir(o)}
                                    >
                                        <i className={`fa ${o.icono}`} aria-hidden="true" />
                                        <span className="buscador-opcion-texto">
                                            <span>{o.titulo}</span>
                                            {o.detalle && <small>{o.detalle}</small>}
                                        </span>
                                        <i className="fa fa-level-down fa-rotate-90 buscador-opcion-enter" aria-hidden="true" />
                                    </button>
                                );
                            })}
                        </div>
                    ))}

                    {consulta && !esperandoRemoto && opciones.length === 0 && (
                        <p className="buscador-vacio">
                            {remoto.error ? 'No se pudo buscar en los registros. Intente nuevamente.' : `Sin resultados para «${consulta}».`}
                        </p>
                    )}
                    {!consulta && opciones.length === 0 && (
                        <p className="buscador-vacio">Escriba el nombre de una pantalla o un código: OP, guía, código ISO, cliente.</p>
                    )}
                </div>

                <div className="buscador-pie">
                    <span><kbd>↑</kbd><kbd>↓</kbd> moverse</span>
                    <span><kbd>Enter</kbd> abrir</span>
                    <span>Desde 3 caracteres busca también dónde aparece el código</span>
                </div>
            </Modal>
        </>
    );
};

export default BuscadorRapido;
